import { Box, Grid, IconButton, TextField, Typography, useMediaQuery } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  ChipLevel,
  CmpButton,
  CmpTypographyHeader,
  EllipsisTooltip
} from '../../../commonComponents/Toobox.js';
import moment from 'moment';
import { Task_ReportApi } from '../../../Action.jsx';
import { useSelector } from 'react-redux';
import { exportToExcel } from '../../../Utility/Utility.jsx';
import { createSelector } from 'reselect';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { zoneOperator } from '../../../commonComponents/DataGridFilterComp.jsx';
import { selectZoneFilter } from '../myWork/TaskList.jsx';

const DailyReport = () => {
  const xMediaQuery = useMediaQuery('(max-width:600px)');
  const aMediaQuery = useMediaQuery('(max-width:900px)');
  const zoneFilter = useSelector(selectZoneFilter);

  const redZonesData = useSelector(
    createSelector(
      (state) => state.user.redZones,
      (redZones) => redZones
    )
  );
  const vLoginData = useSelector(
    createSelector(
      (state) => state.user,
      (user) => user
    )
  );

  const [summaryData, setSummaryData] = useState([]);
  const [loadData, setLoadData] = useState({
    fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    toDate: new Date()
  });

  const handleDateChange = (date, name) => {
    setLoadData((prevState) => ({
      ...prevState,
      [name]: date ? dayjs(date).format('YYYY-MM-DD') : ''
    }));
  };

  const getColor = (value, filter, isZone = false) => {
    if (isZone) {
      let zoneLabel = 'Green';
      if (value >= 90) zoneLabel = 'Red';
      else if (value >= 75) zoneLabel = 'Orange';
      else if (value >= 50) zoneLabel = 'Yellow';

      const foundZone = filter.find((item) => item.label === zoneLabel);
      return foundZone ? foundZone.color : '';
    }
    const foundItem = filter.find((item) => item.label === value);
    return foundItem ? foundItem.color : '';
  };

  const fSummary = useCallback(async () => {
    const obj = {
      User_id: vLoginData?.userCd,
      FromDt: moment(loadData.fromDate).format('MM/DD/YYYY'),
      ToDt: moment(loadData.toDate).format('MM/DD/YYYY')
    };

    const result = await Task_ReportApi(obj);
    console.log('API Result:', result);

    if (result?.status === 200) {
      const rows = result?.data;
      setSummaryData(rows);
    }
  }, [vLoginData, loadData]);

  const rows = summaryData?.map((t) => ({ id: t.T_ID, ...t }));

  //Group rows into Assignee → Priority
  const buildGroupedData = (rows) => {
    const grouped = {};
    rows.forEach((row) => {
      const assignee = row.Assignee || '—';
      const status = row.T_Status || '—';

      if (!grouped[assignee]) grouped[assignee] = {};
      if (!grouped[assignee][status]) grouped[assignee][status] = [];

      grouped[assignee][status].push(row);
    });
    return grouped;
  };

  // Flatten grouped data with indentation for Excel
  const prepareExcelRows = (grouped) => {
    const excelRows = [];

    Object.keys(grouped).forEach((assignee) => {
      excelRows.push({ Desc: assignee }); // level 0

      Object.keys(grouped[assignee]).forEach((status) => {
        excelRows.push({ Desc: '    ' + status }); // level 1

        grouped[assignee][status].forEach((task) => {
          excelRows.push({
            Desc: '        ' + task.T_Desc, // level 2
            DueDate: formatDate(task.T_DueDate),
            Zone: task.FinishedDaysPercentage + '%',
            Supervisor: task.Supervisor,
            Priority: task.T_Priority,
            FollowUpCount: task.FollowUpCount,
            NextFollowUp: task.nextFollowUp,
            CreatedBy: task.CreatedBy,
            CreatedDate: formatDate(task.CreatedDate),
            RecentStatus: task.RecentStatus,
            ClosedDate: formatDate(task.T_Closed_Date),
            LastFollowUp: task.LastFollowUp
          });
        });
      });
    });

    return excelRows;
  };

  const handleExportExcel = () => {
    const grouped = buildGroupedData(summaryData);
    const excelData = prepareExcelRows(grouped);

    exportToExcel(excelData, 'DailyReport_data', 'DailyReport');
  };

  const formatDate = (iso) => {
    if (!iso) return '';

    const date = new Date(iso);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  };

  useEffect(() => {
    fSummary();
  }, [vLoginData?.filterData]);

  const columns = [
    {
      field: 'T_Desc',
      headerName: 'Desc',
      align: 'left',
      headerAlign: 'left',
      width: 360,
      renderCell: (params) => <EllipsisTooltip text={params.value} />
    },
    {
      field: 'T_DueDate',
      headerName: 'DueDate',
      align: 'center',
      headerAlign: 'center',
      width: 100,
      valueFormatter: (p) => formatDate(p.value)
    },

    {
      field: 'T_Closed_Date',
      headerName: 'Completed Date',
      align: 'center',
      headerAlign: 'center',
      width: 150,
      valueFormatter: (p) => formatDate(p.value)
    },

    {
      field: 'FinishedDaysPercentage',
      headerName: 'Zone',
      align: 'center',
      headerAlign: 'center',
      width: 100,
      filterOperators: [zoneOperator],
      renderCell: (params) =>
        params.value == null ? (
          ''
        ) : (
          <ChipLevel value={`${params.value}%`} color={getColor(params.value, zoneFilter, true)} />
        )
    },
    {
      field: 'Supervisor',
      headerName: 'Supervisor',
      align: 'left',
      headerAlign: 'left',
      width: 200
    },
    {
      field: 'FollowUpCount',
      headerName: 'FollowUpCount',
      align: 'center',
      headerAlign: 'center',
      width: 120
    },
    {
      field: 'nextFollowUp',
      headerName: 'NextFollowUp',
      align: 'center',
      headerAlign: 'center',
      width: 120,
      valueFormatter: (p) => formatDate(p.value)
    },

    { field: 'CreatedBy', headerName: 'CreatedBy', align: 'left', headerAlign: 'left', width: 160 },
    {
      field: 'CreatedDate',
      headerName: 'CreatedDate',
      align: 'center',
      headerAlign: 'center',
      width: 120,
      valueFormatter: (p) => formatDate(p.value)
    }
  ];

  return (
    <Box
      className="lead-project"
      sx={{
        height: redZonesData?.length ? '85vh' : '92vh',
        paddingTop: redZonesData?.length ? 0 : xMediaQuery ? 1 : 4
      }}>
      <CmpTypographyHeader xText={'Daily Report'} />
      <Grid
        container
        className="task-detail"
        columnSpacing={{ xs: 1, sm: 2, md: 4 }}
        rowSpacing={xMediaQuery ? 2 : 0}
        sx={{ paddingY: xMediaQuery ? 1 : aMediaQuery ? 0 : 1 }}>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={loadData.fromDate}
              inputFormat="DD/MM/YYYY"
              name="fromDate"
              onChange={(date) => handleDateChange(date, 'fromDate')}
              renderInput={(params) => (
                <TextField {...params} fullWidth id="fromDate" className="duedate" />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={loadData.toDate}
              inputFormat="DD/MM/YYYY"
              name="toDate"
              onChange={(date) => handleDateChange(date, 'toDate')}
              renderInput={(params) => (
                <TextField {...params} fullWidth id="toDate" className="duedate" />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={10} sm={2.8} md={5} lg={7}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'right',
              marginTop: xMediaQuery ? '' : '4px'
            }}>
            <CmpButton xbtnName={'Load'} xOnClick={fSummary} />
          </div>
        </Grid>
        <Grid item xs={2} sm={0.6} md={0.6} lg={0.6} className="exportGrid">
          <Box className="exportBox">
            <IconButton onClick={handleExportExcel}>
              <FileDownloadOutlinedIcon />
            </IconButton>
            <Typography variant="caption" sx={{ mt: -1 }}>
              Export
            </Typography>
          </Box>{' '}
        </Grid>
      </Grid>

      <div style={{ paddingTop: xMediaQuery ? '' : '8px' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          hideFooter
          treeData
          getTreeDataPath={(row) => [
            row.Assignee || '—', // level 0
            row.T_Status || '—', // level 1
            String(row.T_ID) // level 2
          ]}
          groupingColDef={{
            headerName: 'Assignee',
            width: 280
          }}
          initialState={{
            treeData: { defaultGroupingExpansionDepth: 1 }
          }}
          getRowHeight={() => 'auto'}
          className="gridTable"
          sx={{
            borderColor: 'blue',
            border: '3px solid #0d73bb',
            borderRadius: 2,
            height: aMediaQuery ? '65vh' : '74vh',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#c4c4c40f'
            }
          }}
          disableRowSelectionOnClick
        />
      </div>
    </Box>
  );
};

export default DailyReport;
